import React from "react";
import { Facebook, Instagram, Check } from "lucide-react";

/**
 * Pannello opzioni pubblicazione social nel form admin.
 *
 * Props:
 *  - value: { facebook: bool, instagram: bool }
 *  - onChange: (next) => void
 *  - lastSaved: { facebook_url, instagram_url } | null    -> mostra link ai post pubblicati
 *  - isEdit: bool -> in edit mode mostra solo i link ai post gia' pubblicati
 */
const SocialPublishPanel = ({
  value = { facebook: true, instagram: true },
  onChange,
  lastSaved = null,
  isEdit = false,
  onRepublish = null,
  republishing = false,
}) => {
  const toggle = (k) => () => onChange?.({ ...value, [k]: !value[k] });

  const publishedChips =
    lastSaved && (lastSaved.facebook_url || lastSaved.instagram_url) ? (
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        {lastSaved.facebook_url && (
          <a
            href={lastSaved.facebook_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-[#1877F2]/10 text-[#1877F2] px-3 py-1.5 rounded-full font-semibold hover:bg-[#1877F2]/15"
          >
            <Facebook className="w-3 h-3" />
            Vedi post FB
          </a>
        )}
        {lastSaved.instagram_url && (
          <a
            href={lastSaved.instagram_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-[#E4405F]/10 text-[#E4405F] px-3 py-1.5 rounded-full font-semibold hover:bg-[#E4405F]/15"
          >
            <Instagram className="w-3 h-3" />
            Vedi post IG
          </a>
        )}
      </div>
    ) : null;

  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4">
      <p className="text-[11px] font-montserrat font-bold uppercase tracking-widest text-gray-500 mb-3">
        {isEdit ? "Ripubblica sui social (nuovo post)" : "Condividi sui social"}
      </p>

      <div className="grid sm:grid-cols-2 gap-2">
        <label
          className={`flex items-center gap-3 cursor-pointer bg-white rounded-lg border p-3 transition-colors ${
            value.facebook
              ? "border-[#1877F2] shadow-sm"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <input
            type="checkbox"
            checked={!!value.facebook}
            onChange={toggle("facebook")}
            className="sr-only"
          />
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              value.facebook ? "bg-[#1877F2]" : "bg-gray-100"
            }`}
          >
            <Facebook
              className={`w-4 h-4 ${
                value.facebook ? "text-white fill-white" : "text-gray-400"
              }`}
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-montserrat font-semibold text-[#0A1F44]">
              Facebook
            </p>
            <p className="text-[10px] text-gray-500">
              {isEdit ? "Pubblica di nuovo alla pagina" : "Auto-publish alla pagina"}
            </p>
          </div>
          {value.facebook && (
            <Check className="w-4 h-4 text-[#1877F2]" strokeWidth={3} />
          )}
        </label>

        <label
          className={`flex items-center gap-3 cursor-pointer bg-white rounded-lg border p-3 transition-colors ${
            value.instagram
              ? "border-[#E4405F] shadow-sm"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <input
            type="checkbox"
            checked={!!value.instagram}
            onChange={toggle("instagram")}
            className="sr-only"
          />
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              value.instagram
                ? "bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF]"
                : "bg-gray-100"
            }`}
          >
            <Instagram
              className={`w-4 h-4 ${
                value.instagram ? "text-white" : "text-gray-400"
              }`}
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-montserrat font-semibold text-[#0A1F44]">
              Instagram
            </p>
            <p className="text-[10px] text-gray-500">
              Serve almeno un'immagine
            </p>
          </div>
          {value.instagram && (
            <Check className="w-4 h-4 text-[#E4405F]" strokeWidth={3} />
          )}
        </label>
      </div>

      {isEdit && onRepublish && (
        <div className="mt-3">
          <button
            type="button"
            onClick={onRepublish}
            disabled={republishing || (!value.facebook && !value.instagram)}
            className="inline-flex items-center justify-center h-9 px-4 rounded-md border border-[#0A1F44] text-[#0A1F44] text-xs font-montserrat font-bold hover:bg-[#0A1F44] hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#0A1F44] transition-colors"
          >
            {republishing ? "Pubblicazione in corso…" : "Ripubblica solo sui social"}
          </button>
          <p className="text-[11px] text-gray-400 mt-1">
            Non modifica il sito. Usa la versione già salvata (eventuali modifiche non salvate non sono incluse).
          </p>
        </div>
      )}

      {publishedChips}
    </div>
  );
};

export default SocialPublishPanel;
